import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { error } from "app/themes/colors";
import { extractBackendErrorStringMessages } from "utils/common";
import { viteEnv } from "utils/vite-env.util";

type ErrorSnackbarContentProps = {
  response: any;
  title?: string;
  filterMessages?: (messages: string[]) => string[];
  includeFieldName?: boolean;
};

const shouldHideErrors = () =>
  viteEnv.VITE_ENV === "prod" || viteEnv.VITE_ENV === "preprod";

// Builds an opt-in `filterMessages` for backend messages that are safe — and
// necessary — to show verbatim in EVERY environment (e.g. a Claim.MD rejection the
// biller must read to fix the claim). Messages matching `allow` always render; when
// none match, the response keeps the default environment behavior: generic-only in
// prod/preprod (DEV-1730), full detail elsewhere.
export const makeAllowedMessageFilter =
  (allow: (message: string) => boolean) =>
  (messages: string[]): string[] => {
    const allowed = messages.filter(allow);
    if (allowed.length) return allowed;
    return shouldHideErrors() ? [] : messages;
  };

export default function ErrorSnackbarContent({
  title = "The action was unsuccessful due to these errors:",
  response,
  filterMessages,
  includeFieldName = false
}: ErrorSnackbarContentProps) {
  const theme = useTheme();
  let showGenericTextOnly = shouldHideErrors();
  let messages = new Set<string>();
  extractBackendErrorStringMessages(response, messages, includeFieldName);
  if (filterMessages) {
    messages = new Set(filterMessages([...messages]));
    showGenericTextOnly = false;
  }

  const showGenericText = showGenericTextOnly || messages.size === 0;
  const genericText = "An Error has Occurred, please reach out to support.";
  // Never leak auto-extracted backend strings into the prod/preprod generic
  // header. After DEV-1730 widened extractBackendErrorStringMessages to read
  // standard DRF errors, the old `${generic} (${messages})` parenthetical would
  // otherwise surface auth/permission/validation detail across every consumer in
  // prod, which shouldHideErrors() does not suppress. Detail is shown only when a
  // caller explicitly opts in via filterMessages (which clears showGenericTextOnly).
  const headerText = showGenericText ? genericText : title;
  return (
    <Stack spacing={1} zIndex={theme.zIndex.snackbar}>
      <Typography fontWeight={700} color={error[700]}>
        {headerText}
      </Typography>
      {!showGenericTextOnly && (
        <Typography component="ul">
          {[...messages].map((message) => (
            <Typography component="li" key={message} color={error[700]}>
              {message}
            </Typography>
          ))}
        </Typography>
      )}
    </Stack>
  );
}
